import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import type {GetTenantV2ApiRes} from '@dms/api/base';
import type {GetInterestV2ApiResponse, MinipurchaseResponseBody} from '@dms/api/finance';
import type {RentalOrderResponseBody} from '@dms/api/rental';
import type {AiLeadEmailMessageResponseBody, BusinessCaseResponseBody} from '@dms/api/sales';
import type {SaleVehicleResponseBody, VehicleV2ResponseBody} from '@dms/api/vehicle';
import type {
  GetMetadaServiceCaseApiResponse,
  GetServiceOrderApiResponse,
  WorkshopInterestResponseBody,
} from '@dms/api/workshop';

import type {ComposeMessageContext} from '@prompt-bridge/shared/validations';

import type {Nullish} from 'shared/web';

import type {ReplyToMessage} from '../SendCustomerEmailForm/types/ReplyToMessage';

const MAX_FACT_LENGTH = 2000;
const MAX_LABEL_LENGTH = 128;
/** Same cap as the DMS `interest.request` fact: a long lead e-mail must not become the whole prompt. */
const MAX_REQUEST_LENGTH = 1200;
const MAX_INTEREST_VEHICLES = 4;
type VehicleFacts = Pick<VehicleV2ResponseBody, 'title' | 'registrationPlate' | 'vin' | 'mileage'>;

export type AiComposerContextSource =
  | {context: 'CUSTOMER'; data: {name?: string | null}}
  | {context: 'VEHICLE'; data: VehicleFacts}
  | {
      context: 'INTEREST';
      data: Pick<GetInterestV2ApiResponse, 'publicId' | 'state' | 'activeBusinessCases'>;
    }
  | {
      context: 'BUSINESS_CASE';
      data: Pick<BusinessCaseResponseBody, 'code' | 'businessCaseState' | 'offerValidTo'>;
    }
  | {
      context: 'WORKSHOP_INTEREST';
      data: Pick<WorkshopInterestResponseBody, 'publicId' | 'state' | 'scheduledVisit'>;
    }
  | {
      context: 'SERVICE_CASE';
      data: Pick<
        NonNullable<GetMetadaServiceCaseApiResponse>,
        'number' | 'state' | 'totalPriceWithVat' | 'currency'
      >;
    }
  | {
      context: 'SERVICE_ORDER';
      data: Pick<
        NonNullable<GetServiceOrderApiResponse>,
        'number' | 'state' | 'completionAt' | 'totalPrice'
      >;
    }
  | {
      context: 'RENTAL_ORDER';
      data: Pick<RentalOrderResponseBody, 'number' | 'state' | 'plannedRentalPeriod'>;
    };

interface Input {
  source: AiComposerContextSource;
  replyTo?: ReplyToMessage | Nullish;
  recipient: {name?: string | null; preferredLocale?: string | null};
  tenant: Pick<GetTenantV2ApiRes, 'defaultLocale' | 'availableLocales'>;
  translate: (key: string) => string;
  vehicle?: VehicleFacts | Nullish;
  /** The sale vehicle of a business case: its asking price and reservation drive the sale offers. */
  saleVehicle?: Pick<SaleVehicleResponseBody, 'sellingPrice' | 'activeReservation'> | Nullish;
  /**
   * What the customer asked for on an interest, mirrored from the DMS `interest.request` fact: the
   * lead e-mail that opened it and the salesperson's notes on the purchase wish. Only the enquiry
   * offer reads it; the model gets the authoritative value from the DMS ai-context.
   */
  interestRequest?:
    | {
        leadMessage?: Pick<AiLeadEmailMessageResponseBody, 'aiSummary' | 'body'> | Nullish;
        minipurchase?:
          | Pick<MinipurchaseResponseBody, 'additionalInformation' | 'testDriveNote'>
          | Nullish;
      }
    | Nullish;
}

const HTML_TAG = /<[^>]*>/g;
const WHITESPACE_RUN = /\s+/g;

const toText = (value: string | Nullish) => {
  const text = value?.replace(HTML_TAG, ' ').replace(WHITESPACE_RUN, ' ').trim() ?? '';
  return text.length > 0 ? text : null;
};

/** The lead e-mail summary first, else its text, then the notes; capped like the DMS fact. */
function projectInterestRequest(input: NonNullable<Input['interestRequest']>): string | null {
  const parts = [
    toText(input.leadMessage?.aiSummary) ?? toText(input.leadMessage?.body),
    toText(input.minipurchase?.additionalInformation),
    toText(input.minipurchase?.testDriveNote),
  ].filter(isNotNil);
  if (parts.length === 0) {
    return null;
  }
  const request = Array.from(new Set(parts)).join('\n');
  return request.length > MAX_REQUEST_LENGTH
    ? `${request.slice(0, MAX_REQUEST_LENGTH - 1).trimEnd()}…`
    : request;
}

/** Only this projection enters the local context; API response extensions stay private. */
export function projectAiComposerRecordContext(input: Input): ComposeMessageContext | null {
  const {source, recipient, tenant, translate} = input;
  const preferredLocale = tenant.availableLocales.find(
    (locale) => locale === recipient.preferredLocale
  );
  const locale =
    preferredLocale ?? tenant.availableLocales.find((value) => value === tenant.defaultLocale);
  if (isNil(locale)) {
    return null;
  }

  const result: ComposeMessageContext = {
    locale,
    languageSource: isNotNil(preferredLocale) ? 'customer' : 'tenant',
    facts: [],
    unavailableFacts: [],
  };
  const add = (key: string, labelKey: string, value: string | number | Nullish) => {
    const label = translate(labelKey).slice(0, MAX_LABEL_LENGTH);
    const text = match(value)
      .when(
        (value): value is number => typeof value === 'number',
        (value) => (Number.isFinite(value) ? value.toString() : '')
      )
      .otherwise((value) => value?.trim());
    if (isNil(text) || text.length === 0 || text.length > MAX_FACT_LENGTH) {
      result.unavailableFacts.push({key, label});
      return;
    }
    result.facts.push({key, label, value: text});
  };
  const addVehicle = (vehicle: VehicleFacts | Nullish) => {
    add('vehicle.title', 'entity.vehicle.labels.title', vehicle?.title);
    add(
      'vehicle.registrationPlate',
      'entity.vehicle.labels.licensePlate',
      vehicle?.registrationPlate
    );
    add('vehicle.vin', 'entity.vehicle.labels.vin', vehicle?.vin);
    add('vehicle.mileage', 'entity.vehicle.labels.mileage', vehicle?.mileage);
  };
  const addPrice = (
    key: string,
    amount: number | string | Nullish,
    currency: string | Nullish,
    labelKey = 'entity.lineItems.labels.totalPriceWithVat'
  ) => {
    const parsed = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
    const value =
      isNotNil(parsed) &&
      Number.isFinite(parsed) &&
      isNotNil(currency) &&
      /^[A-Z]{3}$/.test(currency)
        ? `${parsed.toString()} ${currency}`
        : null;
    add(key, labelKey, value);
  };
  add('recipient.name', 'entity.email.labels.recipient', recipient.name);
  // The header of the customer's e-mail grounds the reply offer; its body stays on the server.
  if (isNotNil(input.replyTo)) {
    add('replyTo.subject', 'general.labels.subject', input.replyTo.subject);
    add('replyTo.from', 'entity.email.labels.sender', input.replyTo.from);
    add('replyTo.receivedAt', 'general.labels.date', input.replyTo.receivedAt);
  }
  match(source)
    .with({context: 'CUSTOMER'}, (source) => {
      add('customer.name', 'general.labels.customer', source.data.name);
    })
    .with({context: 'VEHICLE'}, (source) => addVehicle(source.data))
    .with({context: 'INTEREST'}, (source) => {
      add('interest.number', 'general.labels.number', source.data.publicId);
      add('interest.state', 'general.labels.status', source.data.state);
      const vehicles = source.data.activeBusinessCases?.slice(0, MAX_INTEREST_VEHICLES);
      if (isNilOrEmpty(vehicles)) {
        add('interest.vehicle', 'entity.vehicle.labels.title', null);
      }
      vehicles?.forEach((businessCase, index) => {
        add(
          `interest.vehicle.${index}`,
          'entity.vehicle.labels.title',
          businessCase.vehicleInfo.title
        );
      });
      if ((source.data.activeBusinessCases?.length ?? 0) > MAX_INTEREST_VEHICLES) {
        add('interest.additionalVehicles', 'entity.vehicle.labels.title', null);
      }
      add('interest.totalPriceWithVat', 'entity.lineItems.labels.totalPriceWithVat', null);
      add(
        'interest.request',
        'general.labels.request',
        isNotNil(input.interestRequest) ? projectInterestRequest(input.interestRequest) : null
      );
    })
    .with({context: 'BUSINESS_CASE'}, (source) => {
      add('businessCase.number', 'general.labels.number', source.data.code);
      add('businessCase.state', 'general.labels.status', source.data.businessCaseState);
      addVehicle(input.vehicle);
      addPrice(
        'businessCase.vehiclePriceWithVat',
        input.saleVehicle?.sellingPrice?.withVat?.amount,
        input.saleVehicle?.sellingPrice?.withVat?.currency,
        'entity.vehicle.labels.sellingPrice'
      );
      add('businessCase.totalPriceWithVat', 'entity.lineItems.labels.totalPriceWithVat', null);
      add(
        'businessCase.onlineOfferValidTo',
        'entity.offerValidityDialog.labels.validTo',
        source.data.offerValidTo
      );
      const reservation = input.saleVehicle?.activeReservation;
      add(
        'activeReservation.reservedUntil',
        'general.labels.validUntil',
        isNotNil(reservation) && !reservation.expired ? reservation.reservedUntil : null
      );
    })
    .with({context: 'WORKSHOP_INTEREST'}, (source) => {
      add('workshopInterest.number', 'general.labels.number', source.data.publicId);
      add('workshopInterest.state', 'general.labels.status', source.data.state);
      add(
        'workshopInterest.scheduledAt',
        'general.labels.date',
        source.data.scheduledVisit?.scheduledAt
      );
      addVehicle(input.vehicle);
    })
    .with({context: 'SERVICE_CASE'}, (source) => {
      add('serviceCase.number', 'entity.serviceCase.labels.number', source.data.number);
      add('serviceCase.state', 'general.labels.status', source.data.state);
      addVehicle(input.vehicle);
      addPrice(
        'serviceCase.totalPriceWithVat',
        source.data.totalPriceWithVat,
        source.data.currency
      );
      add('serviceCase.completionAt', 'entity.order.labels.mandatoryField-completionAtDate', null);
    })
    .with({context: 'SERVICE_ORDER'}, (source) => {
      add('serviceOrder.number', 'general.labels.number', source.data.number);
      add('serviceOrder.state', 'general.labels.status', source.data.state);
      add(
        'serviceOrder.completionAt',
        'entity.order.labels.mandatoryField-completionAtDate',
        source.data.completionAt
      );
      addVehicle(input.vehicle);
      addPrice(
        'serviceOrder.totalPriceWithVat',
        source.data.totalPrice?.withVat?.amount,
        source.data.totalPrice?.withVat?.currency
      );
    })
    .with({context: 'RENTAL_ORDER'}, (source) => {
      add('rentalOrder.number', 'general.labels.number', source.data.number);
      add('rentalOrder.state', 'general.labels.status', source.data.state);
      add(
        'rentalOrder.start',
        'entity.vehicle.labels.startDate',
        source.data.plannedRentalPeriod?.from
      );
      add('rentalOrder.end', 'entity.vehicle.labels.endDate', source.data.plannedRentalPeriod?.to);
    })
    .exhaustive();
  return result;
}
